<template>
    <a :href="homeUrl" class="dark-logo">
        <svg width="179" viewBox="0 0 178.98 30">
            <use :xlink:href="spriteHref('barkoder_logo')"></use>
        </svg>
    </a>
    <div class="formInfo">
        <h1>{{ formHeadings.h1 }}</h1>
        <h2>{{ formHeadings.h2 }}</h2>
    </div>

    <template v-if="showForm == 'recovery'">
        <div class="flex flex-column gap16">
            <div class="form-line with-icon right-icon">
                <label for="back_code">Code</label>
                <input ref="backupCodeInput" class="input white validate" type="text" id="back_code" name="back_code" v-model="backupCode" placeholder="Your Backup Code" autocomplete="off" autocapitalize="off" spellcheck="false" @keydown.enter.prevent="validateBCCode" />
                <span class="mdi mdi-lock-outline"></span>
            </div>
        </div>
        <div class="flex align-center gap16">
            <a class="colors-p-paragraph fs14 red_underline" href="#" @click.prevent="backToLogin">Back to the classic login...</a>
            <button @click.prevent="validateBCCode" type="submit" class="mla app-button red r-pos" :class="{'loader': formLoader}">Continue<span class="mdi mdi-chevron-right"></span></button>
        </div>
    </template>

    <template v-else-if="showForm == 'validateAccount'">
        <div>
            <code-input ref="codeInput" key="validateAccount" @auth-code-entered="validateUserAccountCode"></code-input>
        </div>
        <div class="flex align-center gap16">
            <a class="colors-p-paragraph fs14 red_underline" href="#" @click.prevent="backToLogin">Back to the classic login...</a>
            <button @click.prevent="validateUserAccountCode()" type="submit" class="app-button red mla r-pos" :class="{'loader': formLoader}">Continue<span class="mdi mdi-chevron-right"></span></button>
        </div>
    </template>

    <template v-else-if="showForm == '2fa'">
        <div class="flex flex-column gap16">
            <div class="form-line">
                <code-input ref="codeInput" key="validate2FACode" @auth-code-entered="validate2FACode"></code-input>
            </div>
        </div>
        <div class="flex align-center gap16">
            <a class="colors-p-paragraph fs14 red_underline" @click.prevent="showRecovery" href="#">Use recovery code</a>
            <button @click.prevent="validate2FACode()" type="submit" class="app-button red mla r-pos" :class="{'loader': formLoader}">Continue<span class="mdi mdi-chevron-right"></span></button>
        </div>
    </template>

    <form v-else class="flex gap24 flex-column" method="post" novalidate @submit.prevent="login">
        <div class="flex flex-column gap16">
            <div class="form-line with-icon right-icon" :class="{'error': v$.form.email.$error}">
                <label for="email">E-mail <span class="required">*</span></label>
                <input type="email" id="email" name="email" v-model.trim="v$.form.email.$model" placeholder="E-mail" autocomplete="username" autocapitalize="off" spellcheck="false" @keydown.enter.prevent="login" @blur="v$.form.email.$touch()" />
                <svg viewBox="0 0 24 24"><use :xlink:href="spriteHref('person')"></use></svg>
                <div class="error-message" v-if="invalidLogin && v$.form.email.$error">
                    <span v-if="v$.form.email.required.$invalid">E-mail field is required</span>
                    <span v-else-if="v$.form.email.email.$invalid">This is not a valid e-mail address</span>
                </div>
            </div>
            <div class="form-line with-icon right-icon" :class="{'error': v$.form.password.$error}">
                <label for="password">Password <span class="required">*</span></label>
                <input type="password" id="password" name="password" v-model.trim="v$.form.password.$model" placeholder="Password" autocomplete="current-password" @keydown.enter.prevent="login" @blur="v$.form.password.$touch()" />
                <svg viewBox="0 0 24 24"><use :xlink:href="spriteHref('lock')"></use></svg>
                <div class="error-message" v-if="invalidLogin && v$.form.password.$error">
                    <span v-if="v$.form.password.required.$invalid">Password is required</span>
                    <span v-else-if="v$.form.password.minLength.$invalid">Password must be at least {{ PASSWORD_MIN }} characters</span>
                </div>
            </div>
        </div>
        <div class="flex gap16 align-center flex-wrap">
            <div class="btn_toggle checkbox v2">
                <input type="checkbox" id="rememberMe" name="remember" v-model="form.remember" value="1">
                <label for="rememberMe" class="withInfo oneLineText">
                    Remember me for 30 days
                    <span class="showTooltip info" tabindex="0" aria-label="If unchecked, you'll be signed out after 24 hours." v-tooltip="'If unchecked, you\'ll be signed out after 24 hours.'">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                            <path d="M7.99998 7.33325C7.82317 7.33325 7.6536 7.40349 7.52858 7.52851C7.40355 7.65354 7.33332 7.82311 7.33332 7.99992V10.6666C7.33332 10.8434 7.40355 11.013 7.52858 11.138C7.6536 11.263 7.82317 11.3333 7.99998 11.3333C8.17679 11.3333 8.34636 11.263 8.47139 11.138C8.59641 11.013 8.66665 10.8434 8.66665 10.6666V7.99992C8.66665 7.82311 8.59641 7.65354 8.47139 7.52851C8.34636 7.40349 8.17679 7.33325 7.99998 7.33325ZM8.25332 4.71992C8.09101 4.65324 7.90896 4.65324 7.74665 4.71992C7.66482 4.75165 7.59005 4.79922 7.52665 4.85992C7.46776 4.92472 7.42041 4.99913 7.38665 5.07992C7.34933 5.15904 7.33106 5.2458 7.33332 5.33325C7.33281 5.42099 7.34963 5.50796 7.3828 5.58919C7.41598 5.67041 7.46486 5.74429 7.52665 5.80658C7.59145 5.86547 7.66586 5.91282 7.74665 5.94659C7.84765 5.98808 7.95729 6.00413 8.06595 5.99333C8.1746 5.98252 8.27894 5.9452 8.36979 5.88463C8.46064 5.82406 8.53523 5.74211 8.58699 5.64597C8.63875 5.54982 8.66611 5.44244 8.66665 5.33325C8.66419 5.15674 8.59514 4.98767 8.47332 4.85992C8.40991 4.79922 8.33515 4.75165 8.25332 4.71992ZM7.99998 1.33325C6.68144 1.33325 5.39251 1.72425 4.29618 2.45679C3.19985 3.18933 2.34537 4.23052 1.84079 5.4487C1.3362 6.66687 1.20418 8.00731 1.46141 9.30052C1.71865 10.5937 2.35359 11.7816 3.28594 12.714C4.21829 13.6463 5.40617 14.2813 6.69938 14.5385C7.99259 14.7957 9.33303 14.6637 10.5512 14.1591C11.7694 13.6545 12.8106 12.8 13.5431 11.7037C14.2757 10.6074 14.6666 9.31846 14.6666 7.99992C14.6666 7.12444 14.4942 6.25753 14.1592 5.4487C13.8241 4.63986 13.3331 3.90493 12.714 3.28587C12.095 2.66682 11.36 2.17575 10.5512 1.84072C9.74237 1.50569 8.87546 1.33325 7.99998 1.33325ZM7.99998 13.3333C6.94515 13.3333 5.914 13.0205 5.03694 12.4344C4.15988 11.8484 3.47629 11.0154 3.07263 10.0409C2.66896 9.06636 2.56334 7.994 2.76913 6.95944C2.97492 5.92487 3.48287 4.97456 4.22875 4.22868C4.97463 3.4828 5.92494 2.97485 6.9595 2.76906C7.99407 2.56328 9.06642 2.66889 10.041 3.07256C11.0155 3.47623 11.8485 4.15982 12.4345 5.03688C13.0205 5.91394 13.3333 6.94508 13.3333 7.99992C13.3333 9.41441 12.7714 10.771 11.7712 11.7712C10.771 12.7713 9.41447 13.3333 7.99998 13.3333Z" fill="#6D6D6D"/>
                        </svg>
                    </span>
                </label>
            </div>
            <a class="mla-l colors-p-paragraph fs14 red_underline ws-no-wrap" :href="homeUrl + 'password-reset'" title="Reset Password">Forgot your password?</a>
        </div>
        <div class="pt16 flex justify-center gap16 flex-wrap">
            <button :disabled="v$.$invalid" type="submit" class="app-button red r-pos minW100p-mobile" :class="{'loader': formLoader}">Sign in<svg viewBox="0 0 24 24"><use :xlink:href="spriteHref('arrow_right')"></use></svg></button>
            <div id="googleButton" ref="googleButton"></div>
            <RecaptchaNote />
        </div>
    </form>
</template>

<script setup>
// The sign-in page (support/views/login.php): e-mail and password, then the
// second step when the account asks for one: the code of an authenticator
// app, a backup code, or the code e-mailed after "Continue with Google".
//
// The password and the codes are held only in this component's memory and go
// nowhere but the body of the request that checks them.
import { ref, reactive, computed, watch, nextTick, onMounted } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required, email, minLength } from '@vuelidate/validators';
import { vTooltip } from 'floating-vue';
import 'floating-vue/dist/style.css';
import { notify } from '@admin/helpers.js';
import { siteUrl, post, googleNonce } from '../support_auth_shared/page.js';
import { loadRecaptcha, recaptchaToken, renderGoogleButton, deviceId } from '../support_auth_shared/external.js';
import CodeInput from './CodeInput.vue';
import RecaptchaNote from '../../../../resources/vue_apps/shared_components/RecaptchaNote.vue';

const props = defineProps({
    config: { type: Object, default: () => ({}) },
});

const PASSWORD_MIN = 6;
const RECAPTCHA_FAILED = 'Could not verify reCAPTCHA. Please try again.';
const REQUEST_FAILED = 'An error occurred. Please try again.';

const homeUrl = siteUrl(props.config);
// Where to go after signing in: the page's "r", sent back to the server as it came.
const redirect = String(props.config.redirect || '');

const showForm = ref('login'); // login, 2fa, recovery, validateAccount
const formLoader = ref(false);
const invalidLogin = ref(false);
const locked = ref(false);
const authEmail = ref('');
const backupCode = ref('');

const form = reactive({
    email: '',
    password: '',
    remember: false,
});

const v$ = useVuelidate({
    form: {
        email: { required, email },
        password: { required, minLength: minLength(PASSWORD_MIN) },
    },
}, { form });

const codeInput = ref(null);
const backupCodeInput = ref(null);
const googleButton = ref(null);

const formHeadings = computed(() => {
    if (showForm.value == '2fa') {
        return {
            h1: 'Log in with your authenticator app',
            h2: 'Enter the 6-digit code from your authenticator app to proceed.',
        };
    }
    if (showForm.value == 'validateAccount') {
        return {
            h1: 'Confirm barKoder Account ownership',
            h2: 'Enter the 6-digit code that we\'ve just sent to your email.',
        };
    }
    if (showForm.value == 'recovery') {
        return {
            h1: 'Use Your Backup Code',
            h2: 'Provide your backup code to turn off two-step authentication. This is the 14-character code given to you during setup',
        };
    }
    if (redirect == 'download') {
        return {
            h1: 'Login to download the barKoder SDK & get support',
            h2: 'Login to the barKoder Portal',
        };
    }
    return {
        h1: 'barKoder Portal Account Login',
        h2: 'Login with your barKoder Portal Account to get support',
    };
});

/** The e-mail the second step is for: the one that passed the first step. */
function stepEmail() {
    return authEmail.value || form.email;
}

function goTo(path) {
    window.location.href = homeUrl + (path || 'dashboard');
}

function focusCode() {
    nextTick(() => {
        if (codeInput.value) codeInput.value.focusFirst();
    });
}

function resetCode() {
    nextTick(() => {
        if (codeInput.value) {
            codeInput.value.resetAuthCode();
            codeInput.value.focusFirst();
        }
    });
}

function backToLogin() {
    formLoader.value = false;
    showForm.value = 'login';
}

function showRecovery() {
    showForm.value = 'recovery';
    nextTick(() => {
        if (backupCodeInput.value) backupCodeInput.value.focus();
    });
}

/** A fresh reCAPTCHA token, or false after telling the visitor it failed. */
async function freshToken() {
    try {
        return await recaptchaToken(props.config.recaptchaSiteKey, 'login');
    } catch (error) {
        formLoader.value = false;
        notify('error', RECAPTCHA_FAILED);
        return false;
    }
}

/** This browser's id, or false after telling the visitor it failed. */
async function browserId() {
    try {
        return await deviceId();
    } catch (error) {
        formLoader.value = false;
        notify('error', 'Could not prepare the sign-in form. Please reload the page and try again.');
        return false;
    }
}

/** The six digits to check: the ones the code boxes sent, or what they hold now. */
function enteredCode(authCode) {
    const digits = Array.isArray(authCode) ? authCode : (codeInput.value ? codeInput.value.digits() : []);
    return digits.join('');
}

async function login() {
    v$.value.$touch();

    if (v$.value.$invalid) {
        notify('error', 'Please fix the validation errors.');
        return;
    }

    if (locked.value) {
        notify('error', 'Too many login attempts. Please wait a few minutes before trying again.');
        return;
    }

    if (formLoader.value) return;
    formLoader.value = true;

    const uniqueId = await browserId();
    if (!uniqueId) return;

    const token = await freshToken();
    if (!token) return;

    let result;
    try {
        result = await post(props.config, 'auth/ajax/login', {
            email: form.email,
            password: form.password,
            unique_id: uniqueId,
            remember: form.remember ? 1 : 0,
            r: redirect,
            recaptcha_token: token,
        });
    } catch (error) {
        formLoader.value = false;
        notify('error', REQUEST_FAILED);
        return;
    }

    if (result.status) {
        if (result.valid) {
            goTo(result.redirect_to);
            return;
        }

        // The account asks for a second step
        formLoader.value = false;
        authEmail.value = form.email;
        form.password = '';
        v$.value.$reset();
        showForm.value = '2fa';
        focusCode();
        return;
    }

    invalidLogin.value = true;
    formLoader.value = false;

    if (result.locked) {
        locked.value = true;
    }

    if (result.message) {
        notify('error', result.message);
    }
}

async function validate2FACode(authCode) {
    const code = enteredCode(authCode);

    if (code.length !== 6) {
        notify('error', 'Code must be exactly 6 digits.');
        return;
    }

    if (formLoader.value) return;
    formLoader.value = true;

    const uniqueId = await browserId();
    if (!uniqueId) return;

    const token = await freshToken();
    if (!token) return;

    let result;
    try {
        result = await post(props.config, 'auth/ajax/validate_2FA_and_login', {
            email: stepEmail(),
            code: code,
            unique_id: uniqueId,
            r: redirect,
            recaptcha_token: token,
        });
    } catch (error) {
        formLoader.value = false;
        notify('error', REQUEST_FAILED);
        resetCode();
        return;
    }

    if (result.status) {
        goTo(result.redirect_to);
        return;
    }

    formLoader.value = false;
    notify('error', result.message);
    resetCode();
}

async function validateUserAccountCode(authCode) {
    const code = enteredCode(authCode);

    if (code.length !== 6) {
        notify('error', 'Code must be exactly 6 digits.');
        return;
    }

    if (formLoader.value) return;
    formLoader.value = true;

    const uniqueId = await browserId();
    if (!uniqueId) return;

    const token = await freshToken();
    if (!token) return;

    let result;
    try {
        result = await post(props.config, 'auth/ajax/validateUserAccountCode', {
            email: stepEmail(),
            code: code,
            unique_id: uniqueId,
            r: redirect,
            recaptcha_token: token,
        });
    } catch (error) {
        formLoader.value = false;
        notify('error', REQUEST_FAILED);
        resetCode();
        return;
    }

    if (typeof result.message != 'undefined') {
        notify(result.status ? 'success' : 'error', result.message);
    }

    if (result.status) {
        if (result.valid) {
            goTo(result.redirect_to);
            return;
        }

        // The account also has two-step verification: the authenticator code is next
        formLoader.value = false;
        showForm.value = '2fa';
        focusCode();
        return;
    }

    formLoader.value = false;
    resetCode();
}

async function validateBCCode() {
    if (!backupCode.value) {
        notify('error', 'Enter your Backup Code.');
        return;
    }

    if (formLoader.value) return;
    formLoader.value = true;

    const token = await freshToken();
    if (!token) return;

    let result;
    try {
        result = await post(props.config, 'auth/ajax/validate_BC_code', {
            email: stepEmail(),
            code: backupCode.value,
            recaptcha_token: token,
        });
    } catch (error) {
        formLoader.value = false;
        backupCode.value = '';
        notify('error', REQUEST_FAILED);
        return;
    }

    if (result.status) {
        window.location.href = homeUrl + 'login';
        return;
    }

    formLoader.value = false;
    backupCode.value = '';
    notify('error', result.message);

    nextTick(() => {
        if (backupCodeInput.value) backupCodeInput.value.focus();
    });
}

/** Google's answer after "Continue with Google": the server e-mails a code to confirm. */
async function handleGoogleAccountLogin(response) {
    let uniqueId = '';
    try {
        uniqueId = await deviceId();
    } catch (error) {
        // Sent empty, as when Google answered before the id was ready
    }

    let result;
    try {
        result = await post(props.config, 'auth/ajax/validateGoogleResponse', {
            credential: response && response.credential,
            unique_id: uniqueId,
            r: redirect,
        });
    } catch (error) {
        formLoader.value = false;
        notify('error', REQUEST_FAILED);
        return;
    }

    formLoader.value = false;

    if (result.status) {
        authEmail.value = result.email;
        form.email = result.email;
        showForm.value = 'validateAccount';
        focusCode();
    }

    if (typeof result.message != 'undefined') {
        notify(result.status ? 'success' : 'error', result.message);
    }
}

function drawGoogleButton() {
    renderGoogleButton(googleButton.value, props.config.googleClientId, 'continue_with', handleGoogleAccountLogin, () => googleNonce(props.config))
        .catch(() => { /* No Google button: the form still works */ });
}

// The sign-in form is drawn again when the visitor comes back from a second
// step, and Google's button with it.
watch(showForm, (value) => {
    if (value == 'login') nextTick(drawGoogleButton);
});

onMounted(() => {
    // reCAPTCHA starts early, so the first sign-in does not wait for it. The device id is worked out only when they
    // sign in (24 October 2026: never on page load).
    loadRecaptcha(props.config.recaptchaSiteKey).catch(() => {});
    drawGoogleButton();
});
</script>
